import { useCallback } from 'react'

import clsx from 'clsx'
import { Emoji } from 'emoji-mart-virtualized'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { Image, Space, UploadImagesArgs } from '@tribeplatform/gql-client/types'
import {
  useCreateEmojis,
  useCreateImages,
  useUpdateSpace,
} from '@tribeplatform/react-sdk/hooks'

import { linkToFile } from '../../lib/upload'
import { PopoverImagePicker } from '../Picker/ImagePicker'

const DefaultSpaceEmoji = 'speech_balloon'
const SpaceImageReadOnly = ({
  space,
  size = 'md',
}: {
  space: Space
  size: 'xs' | 'sm' | 'md' | 'lg'
}) => {
  const sizeClsx = [
    size === 'xs' && 'h-6 w-6',
    size === 'sm' && 'h-6 w-6',
    size === 'md' && 'h-24 w-24',
    size === 'lg' && 'h-24 w-24',
  ]
  const sizeInPixel = {
    xs: 24,
    sm: 24,
    md: 96,
    lg: 96,
  }

  const emojiSize = {
    xs: 16,
    sm: 20,
    md: 64,
    lg: 96,
  }

  return (space?.image as Image)?.url ? (
    <div className={clsx('flex items-center justify-center', sizeClsx)}>
      <img
        alt={space.name}
        className={clsx(
          'text-basicSurface-400 group-hover:text-basicSurface-500 object-scale-down',
          'flex-shrink-0 rounded-md',
          sizeClsx,
          size === 'lg' ? 'bg-surface-50 rounded-md p-1' : '',
        )}
        width={sizeInPixel[size] || 24}
        height={sizeInPixel[size] || 24}
        aria-hidden="true"
        src={
          size === 'lg' || size === 'md'
            ? (space?.image as Image)?.urls?.small
            : (space?.image as Image)?.urls?.thumb
        }
      />
    </div>
  ) : (
    <div
      className={clsx(
        'text-basicSurface-400 group-hover:text-basicSurface-500',
        'flex items-center justify-center',
        sizeClsx,
      )}
    >
      <Emoji
        native
        emoji={`:${(space?.image as Emoji)?.text || DefaultSpaceEmoji}:`}
        size={emojiSize[size]}
      />
    </div>
  )
}

export const SpaceImage = ({
  space,
  size = 'md',
  readOnly = true,
}: {
  space: Space
  size?: 'xs' | 'sm' | 'md' | 'lg'
  readOnly?: boolean
}) => {
  const { mutateAsync: createEmojis } = useCreateEmojis()
  const { mutateAsync: createImages } = useCreateImages()
  const { mutate: updateSpace } = useUpdateSpace()
  const [canUpdateSpace] = hasScopesPermission(space, ['updateSpace'])

  const uploadFile = useCallback(
    async file => {
      const args: UploadImagesArgs[] = [{ file }]
      const uploadedImage = await createImages(args)
      if (uploadedImage?.[0]?.id) {
        updateSpace({
          id: space.id,
          input: {
            imageId: uploadedImage[0].id,
          },
        })
      }
    },
    [createImages, updateSpace, space],
  )

  return canUpdateSpace && !readOnly ? (
    <PopoverImagePicker
      tabsProps={{
        file: {
          helper: (
            <>
              <p>Recommended size is 512 x 512 pixels.</p>
              <p>The file size limit is 10MB.</p>
            </>
          ),
        },
      }}
      onLinkChange={async link => {
        const file = await linkToFile(link)
        if (file) uploadFile(file)
      }}
      onImageChange={uploadFile}
      onEmojiChange={async emoji => {
        const uploadedEmoji = await createEmojis([{ text: emoji?.id }])
        updateSpace({
          id: space.id,
          input: {
            imageId: uploadedEmoji[0].id || emoji.id,
          },
        })
      }}
    >
      <SpaceImageReadOnly size={size} space={space} />
    </PopoverImagePicker>
  ) : (
    <SpaceImageReadOnly size={size} space={space} />
  )
}
